/**
 * Lokaso — Cookie consent using vanilla-cookieconsent v3 (self-hosted, MIT).
 *
 * Strategy:
 *  - Always set Google Consent Mode v2 defaults to 'denied' (before GA4 loads).
 *  - Geo-scope: only likely-EU visitors (timezone starts with "Europe/") see the banner.
 *    Non-EU visitors get analytics_storage auto-granted (no friction).
 *  - "Gérer les cookies" footer link opens the preferences modal for ALL users.
 *  - Consent choice persists 6 months in the `cc_cookie`.
 */

import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";
import "./cookieConsent.css";

/** Ensure a gtag stub exists so consent calls always work, even before GA4 loads. */
function ensureGtag() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
  }
}

/** Push Google Consent Mode v2 defaults: everything denied until user opts in. */
export function setConsentDefaults() {
  ensureGtag();
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
}

export function grantAnalytics() {
  ensureGtag();
  window.gtag("consent", "update", { analytics_storage: "granted" });
}

export function denyAnalytics() {
  ensureGtag();
  window.gtag("consent", "update", { analytics_storage: "denied" });
}

/** Timezone-based EU detection (client-side). */
export function isLikelyEU() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    return tz.startsWith("Europe/");
  } catch {
    return false;
  }
}

/** Open the preferences modal (used by "Gérer les cookies" footer link). */
export function openCookiePreferences() {
  try {
    if (CookieConsent.validConsent()) {
      CookieConsent.showPreferences();
    } else {
      CookieConsent.show(true);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[lokaso] openCookiePreferences failed:", e);
  }
}

/** Initialize the consent banner + wire Google Consent Mode v2.
 *  Runs for ALL users so the "Gérer les cookies" link works everywhere.
 *  Non-EU visitors are auto-accepted (no banner). EU visitors see the banner.
 */
let _ccInitStarted = false;
export async function initCookieConsent() {
  if (typeof window === "undefined") return;
  if (_ccInitStarted) return; // React StrictMode calls useEffect twice
  _ccInitStarted = true;

  // 1. Always set defaults BEFORE GA4 script loads.
  setConsentDefaults();

  const euVisitor = isLikelyEU();

  try {
    // 2. Always run the consent library so showPreferences() works for everyone.
    await CookieConsent.run({
    autoShow: euVisitor, // only EU visitors see the banner automatically
    hideFromBots: false, // Guinea/EU users on real browsers only; Playwright reports webdriver=true
    cookie: {
      name: "cc_cookie",
      expiresAfterDays: 182, // ~6 months
    },
    guiOptions: {
      consentModal: {
        layout: "bar",
        position: "bottom",
        equalWeightButtons: false,
        flipButtons: false,
      },
      preferencesModal: {
        layout: "box",
        position: "right",
        equalWeightButtons: false,
        flipButtons: false,
      },
    },
    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {
        enabled: false,
        autoClear: {
          cookies: [
            { name: /^_ga/ },
            { name: "_gid" },
          ],
        },
      },
    },
    onConsent: ({ cookie }) => {
      if (cookie.categories?.includes("analytics")) grantAnalytics();
      else denyAnalytics();
    },
    onChange: ({ cookie }) => {
      if (cookie.categories?.includes("analytics")) grantAnalytics();
      else denyAnalytics();
    },
    language: {
      default: "fr",
      translations: {
        fr: {
          consentModal: {
            title: "Nous respectons votre vie privée",
            description:
              "Lokaso utilise des cookies pour analyser l'audience et améliorer votre expérience. Vous pouvez accepter ou refuser les cookies d'analyse.",
            acceptAllBtn: "Tout accepter",
            acceptNecessaryBtn: "Refuser",
            showPreferencesBtn: "Personnaliser",
          },
          preferencesModal: {
            title: "Préférences des cookies",
            acceptAllBtn: "Tout accepter",
            acceptNecessaryBtn: "Refuser",
            savePreferencesBtn: "Enregistrer mes préférences",
            closeIconLabel: "Fermer",
            sections: [
              {
                title: "Utilisation des cookies",
                description:
                  "Nous utilisons uniquement des cookies strictement nécessaires au fonctionnement du site et, si vous l'acceptez, des cookies d'analyse (Google Analytics 4) pour comprendre comment améliorer Lokaso.",
              },
              {
                title: "Cookies strictement nécessaires",
                description:
                  "Indispensables au fonctionnement du site (session, authentification). Ils ne peuvent pas être désactivés.",
                linkedCategory: "necessary",
              },
              {
                title: "Cookies d'analyse",
                description:
                  "Google Analytics 4 avec IP anonymisée. Nous permet de mesurer l'audience et d'améliorer nos services. Aucune donnée personnelle identifiante n'est collectée.",
                linkedCategory: "analytics",
              },
              {
                title: "Plus d'informations",
                description:
                  'Pour toute question, consultez notre <a href="/politique-cookies">politique de cookies</a> ou écrivez-nous à <a href="mailto:admin@lokasos.com">admin@lokasos.com</a>.',
              },
            ],
          },
        },
      },
    },
  });

  // 3. Non-EU: don't force consent through the library (that skips DOM
  //    generation and breaks showPreferences). Instead grant analytics
  //    directly via Google Consent Mode. Users can still opt out via the
  //    "Gérer les cookies" footer link (which shows the banner on demand).
  if (!euVisitor && !CookieConsent.validConsent()) {
    grantAnalytics();
  }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[lokaso] cc init error:", e);
  }
}
